import { DateTimeFormats } from 'brock-date-time';
import uuid from 'uuid/v4';

export const defaultViewKey = 'default';

export const gridRowHeightPx = 38;
export const gridRowHeightPxIE11 = gridRowHeightPx + 18;
export const defaultGridRows = 12;
export const defaultGridColumns = 12;

export const editorGridRowHeightPx = 52;
export const editorMinWidgetCols = 2;
export const editorMinWidgetRows = 3;
export const editorDrawerWidth = 405;
export const editorDrawerBreakpoint = 768;

export const hoursInADay = 24;
export const hoursInTenDays = hoursInADay * 10;
export const hoursInAMonth = hoursInADay * 30;
export const hoursInAYear = hoursInADay * 365;
export const hoursInTwoYears = hoursInAYear * 2;

const minutesInAnHour = 60;
const minutesInADay = minutesInAnHour * 24;
const minutesInAnWeek = minutesInADay * 7;

export const precisionMinutes = {
    minute: 1,
    hour: minutesInAnHour,
    day: minutesInADay,
    week: minutesInAnWeek,
};

export const dateFormat = DateTimeFormats.DATE;
export const dateTimeFormat = DateTimeFormats.DATETIME;

export const timeSeriesXAxis = 'timestamp';

export const lineGraphDotSize = 6;

export const lineGridStyle = '3 3';

export const iOSResponsiveContainerHeight = 260;

// export const originalColors = [
//     '#1D9BFF',
//     '#FA7921',
//     '#A5FFD6',
//     '#9272FF',
//     '#FDE74C',
//     '#A0A0A0',
//     '#5BC0EB',
//     '#FFD151',
//     '#B471FF',
//     '#B5DBC2',
//     '#B2B3B7',
//     '#68D9FF',
//     '#FFB379',
//     '#B89AFF',
//     '#A4DB6A',
//     '#BCBCBC',
//     '#95DBFF',
//     '#BC8668',
//     '#95A4E7',
//     '#FF47A1',
//     '#D5CCE0',
//     '#D6BDD9',
//     '#1D9BFF',
//     '#FF47A1',
//     '#A5FFD6',
//     '#A0A0A0',
//     '#5BC0EB'
// ];

export const colors = [
    '#66709F',
    '#FF7F46',
    '#00C0C7',
    '#FF6589',
    '#6275F0',
    '#037245',
    '#50AFFF',
    '#E8871A',
    '#6F38B1',
    '#47E26F',
    '#DA3490',
    '#268D6C',
    '#5144D3',
    '#DFBF03',
    '#9089FA',
    '#9BEC54',
    '#9A00C7',
    '#5075C9',
];

export const metricRequestDefaults = {
    metricSourceKey: '',
    timeAndPrecision: {},
    recordLimit: 0,
    metrics: [],
    where: [],
    groupBy: [],
    having: [],
    aggregate: '',
    filters: {},
    isDescending: false,
};

export const inputTypes = {
    string: 'string',
    singleSelect: 'singleSelect',
    multiSelect: 'multiSelect',
    unconstrainedMultiSelect: 'unconstrainedMultiSelect',
    treeSelect: 'treeSelect',
};

export const precisionTypes = {
    minute: 'minute',
    hour: 'hour',
    day: 'day',
    week: 'week',
    month: 'month',
    quarter: 'quarter',
    year: 'year',
};

export const widgetContextMenuActionTypes = {
    filter: 'filter',
    link: 'link',
};

export const noDataIdentifier = '−';

export const groupAxisFormatTypes = {
    dateMonthShort: 'dateMonthShort',
    dateMonthFull: 'dateMonthFull',
    dateConfigured: 'dateConfigured',
};

export const timeRangeConfigurationKeys = {
    timeRangeType: 'timeRangeType',
    predefinedDateRange: 'predefinedDateRange',
    rangeValue: 'rangeValue',
    rangeType: 'rangeType',
    startDateTime: 'startDateTime',
    endDateTime: 'endDateTime',
};

export const timeAndPrecisionConfigurationKeys = {
    timeAndPrecisionType: 'timeAndPrecisionType',
    predefinedDateRange: 'predefinedDateRange',
    rangeValue: 'rangeValue',
    rangeType: 'rangeType',
    startDateTime: 'startDateTime',
    endDateTime: 'endDateTime',
    precisionType: 'precisionType',
    precisionValue: 'precisionValue',
};

export const predicateValues = {
    isNot: 'isNot',
    is: 'is',
    contains: 'contains',
    beginsWith: 'beginsWith',
    endsWith: 'endsWith',
    notIn: 'notIn',
    isIn: 'isIn',
    greaterThan: 'greaterThan',
    greaterThanOrEqualTo: 'greaterThanOrEqualTo',
    lessThan: 'lessThan',
    lessThanOrEqualTo: 'lessThanOrEqualTo',
};

export const predicateMappings = {
    2: '=',
    10: '<',
    11: '<=',
    8: '>',
    9: '>=',
};

export const MAX_CHART_LABEL_WIDTH = 150;
export const MIN_CHART_LABEL_WIDTH = 50;

export const defaultMaxWidth = '1900px';
export const viewConfigurationDefaults = {
    additionalFilterKeys: [],
    availableFilterKeys: [],
    canEmail: false,
    canManualRefresh: true,
    collapseOnApply: true,
    containerStyles: { maxWidth: defaultMaxWidth },
    defaultNumberDecimalFormatterKey: 'noDecimal',
    filterValues: {},
    friendlyColumnLabels: {},
    isDescending: false,
    numberOfGridRows: defaultGridRows,
    openLinksInNewTab: true,
    onlineHelpId: undefined,
    panels: [],
    predefinedDateRanges: [],
    recordLimit: '',
    refreshRateMilliseconds: 60000,
    requiredFilterKeys: [],
    showMeasureFilters: false,
    showPrecisionConfig: true,
    showRecordLimit: false,
    showShareAction: true,
    showSsiWarning: false,
    showTimeAndPrecision: true,
    siteMapKey: undefined,
    subtitle: '',
    timeAndPrecisionTypeOptions: undefined,
    title: '',
    whereClauses: [],
    whereClauseOptions: [],
    widgets: [],
    features: {
        showPortraitPrint: true,
        showLandscapePrint: true,
    },
};

export function widgetConfigurationDefaults() {
    return {
        additionalFilterKeys: [],
        availableFilterKeys: [],
        filterValues: {},
        friendlyLegendLabels: [],
        id: uuid(),
        isDescending: false,
        predefinedDateRanges: [],
        recordLimit: '',
        showMeasureFilters: false,
        showPrecisionConfig: true,
        showRecordLimit: false,
        showTimeAndPrecision: false,
        timeAndPrecisionTypeOptions: undefined,
        whereClauses: [],
        whereClauseOptions: [],
    };
}

export const printOrientation = {
    landscape: 'landscape',
    portrait: 'portrait',
};

export const errorTimeout = 5000;

export const minPieLabelDisplayPercentage = 0.02;

export const responsiveLayouts = {
    auto: 'autoAdjustLayout',
    mobile: 'singleColumnLayout',
    tablet: 'twoColumnLayout',
    desktop: 'desktopLayout',
};
export const keyDownEventCodes = [13, 32];

export const minWindowWidths = {
    tablet: 768,
    desktop: 1440,
};

export const maxPrecisionValue = 9999;

export const widgetTypes = {
    barGraph: 'barGraph',
    lineGraph: 'lineGraph',
    gauge: 'gauge',
    numberWithIndicator: 'numberWithIndicator',
    pieChart: 'pieChart',
    sankey: 'sankey',
    selector: 'selector',
    sparkline: 'sparkline',
    statusCells: 'statusCells',
    table: 'table',
};

export const queryKeys = {
    getMetricData: 'getMetricData',
};

export const downloadToastDisplayTime = 3000;

export const customDashboardModes = {
    edit: 'edit',
    view: 'view',
};

export const editorTabs = {
    widgetLibrary: 'widgetLibrary',
    editWidget: 'editWidget',
};

export const standardPredefinedDateRanges = [
    {
        name: 'yesterday',
        predefinedType: 'day',
        predefinedValue: -1,
        predefinedLength: 1,
    },
    {
        name: 'today',
        predefinedType: 'day',
        predefinedValue: 0,
        predefinedLength: 1,
    },
    {
        name: 'lastWeek',
        predefinedType: 'week',
        predefinedValue: -1,
        predefinedLength: 1,
    },
    {
        name: 'thisWeek',
        predefinedType: 'week',
        predefinedValue: 0,
        predefinedLength: 1,
    },
    {
        name: 'thisMonth',
        predefinedType: 'month',
        predefinedValue: 0,
        predefinedLength: 1,
    },
    {
        name: 'lastMonth',
        predefinedType: 'month',
        predefinedValue: -1,
        predefinedLength: 1,
    },
    {
        name: 'thisQuarter',
        predefinedType: 'quarter',
        predefinedValue: 0,
        predefinedLength: 1,
    },
    {
        name: 'lastQuarter',
        predefinedType: 'quarter',
        predefinedValue: -1,
        predefinedLength: 1,
    },
    {
        name: 'thisYear',
        predefinedType: 'year',
        predefinedValue: 0,
        predefinedLength: 1,
    },
    {
        name: 'lastYear',
        predefinedType: 'year',
        predefinedValue: -1,
        predefinedLength: 1,
    },
];

/* eslint-disable */
export const emailRegex =
    /^((([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))[ ,;]*)*$/;
/* eslint-enable */

export const libraryViews = {
    dashboards: 'dashboards',
    search: 'search',
    widgets: 'widgets',
};

export const defaultUserPreferences = {
    savedDashboards: [],
    savedSearches: [],
};
